import React, { useState, useMemo, useRef, useEffect } from "react";
import DraggablePanel from "./DraggablePanel";
import AnimationPlayer from "./AnimationPlayer";
import { Plus, Trash2, GripVertical } from "lucide-react";
import Tooltip from "../ui/Tooltip";
import MenuSelect from "../ui/MenuSelect";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useBodies, useWorldBodies, useJoints } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";

const KEYFRAME_OWNER_SEPARATOR = " :: ";
const EMPTY_OBJECT = Object.freeze({});

const normalizeNameForMatch = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const inferBaseValueCount = (qposLength, jointCount) => {
  if (!Number.isFinite(qposLength) || !Number.isFinite(jointCount)) return null;
  if (jointCount < 0 || qposLength < 0) return null;

  if (qposLength === jointCount) return 0;
  if (qposLength === jointCount + 7) return 7;
  if (qposLength === jointCount + 3) return 3;

  const diff = qposLength - jointCount;
  if (diff > 0 && diff <= 7) return diff;
  return null;
};

const keyframeNameMatchesOwner = (keyframeName, ownerName) => {
  const normalizedKeyframe = normalizeNameForMatch(keyframeName);
  const normalizedOwner = normalizeNameForMatch(ownerName);
  if (!normalizedKeyframe || !normalizedOwner) return false;

  if (normalizedKeyframe.includes(normalizedOwner)) return true;

  // Also match meaningful owner tokens such as "ur5e" in "pick and place (ur5e)".
  const ownerTokens = normalizedOwner
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);
  return ownerTokens.some((token) => normalizedKeyframe.includes(token));
};

function SortableKeyframe({
  name,
  displayName,
  ownerName,
  showOwnerLabel = false,
  isActive = false,
  onDeleteKeyframe,
  onClick,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: name,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2">
      {/* Drag handle */}
      <Tooltip content="Drag to reorder">
        <button
          {...attributes}
          {...listeners}
          className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-300 cursor-grab active:cursor-grabbing transition-colors duration-150"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-3 h-3" />
        </button>
      </Tooltip>

      <div className="group relative flex-1">
        {/* Keyframe button */}
        <button
          type="button"
          onClick={onClick}
          className={`menu-item flex w-full items-center gap-2 justify-start px-3 py-2 pr-8 select-none focus:outline-none rounded relative ${
            isActive
              ? "bg-green-900/20 text-green-300 border border-green-600"
              : "text-gray-200 hover:bg-gray-800/50"
          }`}
        >
          <span className="flex-1 text-left relative z-10">
            <span className="block">{displayName || name}</span>
            {showOwnerLabel && (
              <span className="block text-[11px] panel-text-helper mt-0.5">
                {ownerName || "Scene"}
              </span>
            )}
          </span>
        </button>
        <Tooltip content="Delete keyframe">
          <button
            type="button"
            aria-label="Delete keyframe"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteKeyframe(e, name);
            }}
            className="absolute right-3 top-1/2 z-10 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded bg-[var(--panel-surface-hover)] text-white opacity-0 transition-colors duration-150 hover:!bg-red-500 focus:opacity-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300 group-hover:opacity-100"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

const PoseEditor = ({
  open,
  onClose,
  keyframes = {},
  handleKeyframeSelect,
  selectedKeyframe,
  onCreateKeyframe,
  onDeleteKeyframe,
  interpolateKeyframes,
  titleIcon,
  mobilePinPanelId,
}) => {
  const keyframeNames = Object.keys(keyframes ?? EMPTY_OBJECT);
  const keyframeCount = keyframeNames.length;
  const bodies = useBodies();
  const worldBodies = useWorldBodies();
  const jointsMap = useJoints();

  // Check if we have a robot loaded by checking if there are bodies
  const hasRobotLoaded = Object.keys(bodies ?? EMPTY_OBJECT).length > 0;

  // Animation state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentKeyframeIndex, setCurrentKeyframeIndex] = useState(null);
  const [interpolationProgress, setInterpolationProgress] = useState(0);
  const [loopAnimation, setLoopAnimation] = useState(true);

  // Track custom ordering for drag-and-drop
  const [customOrder, setCustomOrder] = useState(null);
  const [createOwnerSelection, setCreateOwnerSelection] = useState("__scene__");

  // Scroll container ref for keyframes
  const scrollContainerRef = useRef(null);
  // Derive ordered keyframes from keyframeNames and custom order
  const orderedKeyframes = useMemo(() => {
    if (customOrder) {
      // Use custom order but filter out any keyframes that no longer exist
      return customOrder.filter((name) => keyframeNames.includes(name));
    }
    // Default order is just the keyframe names as they come
    return [...keyframeNames];
  }, [keyframeNames, customOrder]);

  const ownerContexts = useMemo(() => {
    const bodyMap = bodies || {};
    const allJoints = Object.values(jointsMap || {});
    const worldEntries = Array.isArray(worldBodies) ? worldBodies : [];

    return worldEntries
      .map((entry) => {
        const ownerUuid = typeof entry === "string" ? entry : entry?.uuid;
        if (!ownerUuid || !bodyMap[ownerUuid]) return null;

        const ownerName =
          (typeof entry === "string" ? null : entry?.name) || bodyMap[ownerUuid]?.name || ownerUuid;
        const jointUuidSet = new Set();
        const visited = new Set();
        const stack = [ownerUuid];

        while (stack.length > 0) {
          const bodyUuid = stack.pop();
          if (!bodyUuid || visited.has(bodyUuid)) continue;
          visited.add(bodyUuid);
          const body = bodyMap[bodyUuid];
          if (!body) continue;

          (body.joints || []).forEach((jointUuid) => {
            if (jointUuid) jointUuidSet.add(String(jointUuid));
          });
          (body.children || []).forEach((childUuid) => {
            if (childUuid && !visited.has(String(childUuid))) {
              stack.push(String(childUuid));
            }
          });
        }

        const joints = allJoints.filter((joint) => jointUuidSet.has(String(joint.uuid)));
        return { ownerUuid, ownerName, joints };
      })
      .filter(Boolean);
  }, [bodies, jointsMap, worldBodies]);

  const keyframeEntries = useMemo(() => {
    return orderedKeyframes.map((fullName) => {
      const qpos = keyframes[fullName];
      let ownerName = "Scene";
      let ownerUuid = null;
      let displayName = fullName;

      const separatorIndex = fullName.indexOf(KEYFRAME_OWNER_SEPARATOR);
      if (separatorIndex > 0) {
        const parsedOwnerName = fullName.slice(0, separatorIndex).trim();
        const parsedPoseName = fullName
          .slice(separatorIndex + KEYFRAME_OWNER_SEPARATOR.length)
          .trim();
        displayName = parsedPoseName || fullName;
        const matchedOwner = ownerContexts.find((ctx) => ctx.ownerName === parsedOwnerName);
        ownerName = parsedOwnerName;
        ownerUuid = matchedOwner?.ownerUuid || null;
      } else if (Array.isArray(qpos) && ownerContexts.length > 0) {
        const ownersByNameHint = ownerContexts.filter((ctx) =>
          keyframeNameMatchesOwner(fullName, ctx.ownerName)
        );
        if (ownersByNameHint.length === 1) {
          ownerName = ownersByNameHint[0].ownerName;
          ownerUuid = ownersByNameHint[0].ownerUuid;
        } else {
          const structurallyMatchingOwners = ownerContexts.filter((ctx) => {
            const jointCount = Array.isArray(ctx.joints) ? ctx.joints.length : 0;
            if (jointCount <= 0) return false;
            const baseValues = inferBaseValueCount(qpos.length, jointCount);
            return baseValues !== null && baseValues + jointCount === qpos.length;
          });
          if (structurallyMatchingOwners.length === 1) {
            ownerName = structurallyMatchingOwners[0].ownerName;
            ownerUuid = structurallyMatchingOwners[0].ownerUuid;
          }
        }
      }

      return { fullName, displayName, ownerName, ownerUuid };
    });
  }, [orderedKeyframes, keyframes, ownerContexts]);

  const keyframeEntryByName = useMemo(() => {
    const byName = new Map();
    keyframeEntries.forEach((entry) => byName.set(entry.fullName, entry));
    return byName;
  }, [keyframeEntries]);

  const groupedKeyframes = useMemo(() => {
    const groups = [];
    const byOwner = new Map();
    keyframeEntries.forEach((entry) => {
      if (!byOwner.has(entry.ownerName)) {
        const group = { ownerName: entry.ownerName, entries: [] };
        byOwner.set(entry.ownerName, group);
        groups.push(group);
      }
      byOwner.get(entry.ownerName).entries.push(entry);
    });
    return groups;
  }, [keyframeEntries]);

  const createOwnerOptions = useMemo(() => {
    return [
      { value: "__scene__", label: "Scene" },
      ...ownerContexts.map((ctx) => ({
        value: ctx.ownerUuid,
        label: ctx.ownerName,
      })),
    ];
  }, [ownerContexts]);

  useEffect(() => {
    const hasSelection = createOwnerOptions.some((option) => option.value === createOwnerSelection);
    if (!hasSelection) {
      setCreateOwnerSelection("__scene__");
    }
  }, [createOwnerOptions, createOwnerSelection]);

  useEffect(() => {
    if (keyframeCount !== 0) return;
    setCustomOrder(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentKeyframeIndex(null);
    setInterpolationProgress(0);
  }, [keyframeCount]);

  // TEMPORARY: Add test keyframes to force overflow
  const testKeyframes = useMemo(() => {
    const test = [];
    for (let i = 1; i <= 20; i++) {
      test.push(`Test Keyframe ${i}`);
    }
    return [...orderedKeyframes, ...test];
  }, [orderedKeyframes]);

  const handleScopedKeyframeSelect = (keyframeName) => {
    const entry = keyframeEntryByName.get(keyframeName);
    handleKeyframeSelect(keyframeName, entry?.ownerUuid || undefined);
  };

  const handleScopedInterpolate = (keyframeName1, keyframeName2, factor) => {
    const owner1 = keyframeEntryByName.get(keyframeName1)?.ownerUuid || null;
    const owner2 = keyframeEntryByName.get(keyframeName2)?.ownerUuid || null;
    let scopedOwnerUuid;
    if (keyframeName1 === "current") {
      scopedOwnerUuid = owner2 || undefined;
    } else if (keyframeName2 === "current") {
      scopedOwnerUuid = owner1 || undefined;
    } else if (owner1 && owner1 === owner2) {
      scopedOwnerUuid = owner1;
    }
    interpolateKeyframes(keyframeName1, keyframeName2, factor, scopedOwnerUuid);
  };

  const handleCreateKeyframe = () => {
    const selectedOwnerLabel =
      createOwnerSelection === "__scene__"
        ? "Scene"
        : createOwnerOptions.find((option) => option.value === createOwnerSelection)?.label ||
          "Scene";

    const prefixedExisting = new Set(
      keyframeNames
        .filter((name) => name.startsWith(`${selectedOwnerLabel}${KEYFRAME_OWNER_SEPARATOR}`))
        .map((name) => name.slice(`${selectedOwnerLabel}${KEYFRAME_OWNER_SEPARATOR}`.length))
    );

    // Find the next available number for the keyframe name
    let counter = 1;
    let newName = `new keyframe ${counter}`;

    while (prefixedExisting.has(newName)) {
      counter++;
      newName = `new keyframe ${counter}`;
    }

    onCreateKeyframe(
      newName,
      createOwnerSelection === "__scene__" ? undefined : createOwnerSelection
    );
  };

  const handleDeleteKeyframe = (e, keyframeName) => {
    e.stopPropagation(); // Prevent triggering the keyframe selection
    onDeleteKeyframe(keyframeName);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      setCustomOrder((prevOrder) => {
        const currentOrder = prevOrder || keyframeNames;
        const oldIndex = currentOrder.indexOf(active.id);
        const newIndex = currentOrder.indexOf(over.id);
        return arrayMove(currentOrder, oldIndex, newIndex);
      });
    }
  };

  // ResizeObserver to handle dynamic scrollbar updates
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(() => {
      // Simple timeout to ensure DOM updates are complete
      const timer = setTimeout(() => {
        if (scrollContainerRef.current) {
          // Trigger a scroll event to update scrollbar visibility
          scrollContainerRef.current.dispatchEvent(new Event("scroll"));

          // Debug logging
          logger.info("Keyframes scroll container dimensions (resize):", {
            scrollHeight: scrollContainerRef.current.scrollHeight,
            clientHeight: scrollContainerRef.current.clientHeight,
            offsetHeight: scrollContainerRef.current.offsetHeight,
            hasOverflow:
              scrollContainerRef.current.scrollHeight > scrollContainerRef.current.clientHeight,
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    });

    resizeObserver.observe(scrollContainerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Force scroll container update when keyframes change
  useEffect(() => {
    // Simple timeout to ensure DOM updates are complete
    const timer = setTimeout(() => {
      if (scrollContainerRef.current) {
        // Trigger a scroll event to update scrollbar visibility
        scrollContainerRef.current.dispatchEvent(new Event("scroll"));

        // Debug logging
        logger.info("Keyframes scroll container dimensions:", {
          scrollHeight: scrollContainerRef.current.scrollHeight,
          clientHeight: scrollContainerRef.current.clientHeight,
          offsetHeight: scrollContainerRef.current.offsetHeight,
          hasOverflow:
            scrollContainerRef.current.scrollHeight > scrollContainerRef.current.clientHeight,
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [orderedKeyframes]);

  // Debug: Log container dimensions on mount and after changes
  useEffect(() => {
    const logDimensions = () => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        logger.info("🔍 Keyframes Container Debug:", {
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          offsetHeight: container.offsetHeight,
          hasOverflow: container.scrollHeight > container.clientHeight,
          computedStyle: window.getComputedStyle(container),
          overflowY: window.getComputedStyle(container).overflowY,
          height: window.getComputedStyle(container).height,
          minHeight: window.getComputedStyle(container).minHeight,
        });
      }
    };

    // Log on mount
    logDimensions();

    // Log after a delay to ensure DOM is ready
    const timer = setTimeout(logDimensions, 500);

    return () => clearTimeout(timer);
  }, [testKeyframes]);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Pose Editor"
      titleIcon={titleIcon}
      initialSize={{ width: 340, height: 500 }}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex flex-col h-full w-full p-3 gap-4 text-gray-100 text-sm">
        {!hasRobotLoaded && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="text-gray-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto mb-4 opacity-50"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Robot Loaded</h3>
            <p className="text-sm text-gray-400 max-w-xs">
              Load a robot to create and manage keyframes for animations.
            </p>
          </div>
        )}

        {hasRobotLoaded && (
          <>
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-3 gap-2">
                <div className="font-semibold">
                  Keyframes <span className="text-gray-400">({keyframeCount})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip content="Create keyframe for">
                    <MenuSelect
                      size="compact"
                      value={createOwnerSelection}
                      onChange={setCreateOwnerSelection}
                      className="bg-gray-800/60 border-gray-700"
                      triggerClassName="shadow-none"
                      options={createOwnerOptions.map((option) => ({
                        value: option.value,
                        label: option.label,
                      }))}
                      contentClassName="max-h-[min(240px,50vh)]"
                      triggerProps={{
                        "aria-label": "Create keyframe for",
                        onPointerDown: (e) => e.stopPropagation(),
                      }}
                    />
                  </Tooltip>
                  <button
                    onClick={handleCreateKeyframe}
                    className="menu-button flex items-center gap-2 px-3 py-1 text-xs border border-gray-700 rounded hover:border-gray-600 transition-colors duration-150"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
              </div>
              {/* Group keyframes by owning robot/object when available */}
              <div className="py-1 pr-2 space-y-3">
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  {groupedKeyframes.map((group) => (
                    <div key={group.ownerName} className="space-y-1">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">
                        {group.ownerName}
                      </div>
                      <SortableContext
                        items={group.entries.map((entry) => entry.fullName)}
                        strategy={verticalListSortingStrategy}
                      >
                        {group.entries.map((entry) => (
                          <SortableKeyframe
                            key={entry.fullName}
                            name={entry.fullName}
                            displayName={entry.displayName}
                            ownerName={entry.ownerName}
                            showOwnerLabel={group.ownerName === "Scene"}
                            isActive={
                              (isPlaying &&
                                currentKeyframeIndex ===
                                  orderedKeyframes.indexOf(entry.fullName)) ||
                              selectedKeyframe === entry.fullName
                            }
                            onDeleteKeyframe={handleDeleteKeyframe}
                            onClick={() => handleScopedKeyframeSelect(entry.fullName)}
                          />
                        ))}
                      </SortableContext>
                    </div>
                  ))}
                </DndContext>
              </div>
            </div>

            {/* Animation Controls */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="font-semibold mb-1">Animate</div>
              <AnimationPlayer
                keyframeNames={orderedKeyframes}
                interpolateKeyframes={handleScopedInterpolate}
                handleKeyframeSelect={handleScopedKeyframeSelect}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                currentTime={currentTime}
                setCurrentTime={setCurrentTime}
                currentKeyframeIndex={currentKeyframeIndex}
                setCurrentKeyframeIndex={setCurrentKeyframeIndex}
                interpolationProgress={interpolationProgress}
                setInterpolationProgress={setInterpolationProgress}
                loopAnimation={loopAnimation}
                setLoopAnimation={setLoopAnimation}
                showProgressBar={true}
                useMenuStyle={true}
              />
            </div>
          </>
        )}
      </div>
    </DraggablePanel>
  );
};

export default PoseEditor;
